// src/contexts/ExamContext.jsx
import {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";
import {StudentAnswerService} from "../service/StudentAnswerService";

const ExamContext = createContext();

export const useExam = () => useContext(ExamContext);

export const ExamProvider = ({ exam, studentExamId, children }) => {
    // Single source: studentAnswers (List<StudentAnswer> each contains question, objectIndex, existing id, answer info, dirty flag)
    const [studentAnswers, setStudentAnswers] = useState([]);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const questions = useMemo(() => studentAnswers.map(sa => sa.question).filter(Boolean), [studentAnswers]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

    // review / UI auxiliary states
    const [reviewMarks, setReviewMarks] = useState([]);
    const [disabledOptions, setDisabledOptions] = useState({});
    const [questionHtmlOverrides, setQuestionHtmlOverrides] = useState({});
    const [optionHtmlOverrides, setOptionHtmlOverrides] = useState({});
    const [startTime] = useState(Date.now());
    const studentAnswerService = useMemo(() => new StudentAnswerService(), []);

    // Derived simple answers map (legacy consumers expect answers[questionId])
    const answers = useMemo(() => {
        const map = {};
        for (const sa of studentAnswers) {
            const qId = sa?.question?.id;
            if (!qId) continue;
            if (sa?.question?.questionType === 'OpenEnded' || sa?.question?.type === 'OpenEnded') {
                if (sa.answerText) map[qId] = sa.answerText; // raw text
            } else {
                if (Array.isArray(sa.selectedOptionIds) && sa.selectedOptionIds.length > 0) {
                    map[qId] = sa.selectedOptionIds[0]; // single-select assumption
                }
            }
        }
        return map;
    }, [studentAnswers]);

    // Helpers ------------------------------------------------------
    const findIndexByQuestionId = (questionId) => studentAnswers.findIndex(sa => sa?.question?.id === questionId);
    const cloneAndSet = (updater) => setStudentAnswers(prev => {
        const next = [...prev];
        updater(next);
        return next;
    });

    // Option based answer set / toggle
    const setAnswer = (questionId, optionId) => {
        if (!questionId) return;
        cloneAndSet(list => {
            const idx = findIndexByQuestionId(questionId);
            if (idx < 0) return;
            const sa = { ...list[idx] };
            const prevSel = Array.isArray(sa.selectedOptionIds) ? sa.selectedOptionIds : [];
            const current = prevSel[0];
            if (current === optionId || optionId == null) {
                sa.selectedOptionIds = []; // clear
            } else {
                sa.selectedOptionIds = [optionId];
            }
            sa.answerText = null; // not open ended when selecting option
            sa.dirty = true;
            list[idx] = sa;
        });
    };

    // Open ended answer set (does not persist immediately unless navigation triggers persistOpenAnswer)
    const setOpenAnswer = (questionId, text) => {
        if (!questionId) return;
        cloneAndSet(list => {
            const idx = findIndexByQuestionId(questionId);
            if (idx < 0) return;
            const sa = { ...list[idx] };
            sa.answerText = text;
            sa.selectedOptionIds = []; // ensure option list cleared
            sa.dirty = true;
            list[idx] = sa;
        });
    };

    const clearAnswer = (questionId) => {
        if (!questionId) return;
        cloneAndSet(list => {
            const idx = findIndexByQuestionId(questionId);
            if (idx < 0) return;
            const sa = { ...list[idx], answerText: null, selectedOptionIds: [], dirty: true };
            list[idx] = sa;
        });
    };

    const toggleReviewMark = (questionId) => {
        setReviewMarks(prev => ({ ...prev, [questionId]: !prev?.[questionId] }));
    };

    const toggleOptionDisabled = (questionId, optionId) => {
        setDisabledOptions(prev => {
            const qMap = { ...(prev?.[questionId] || {}) };
            if (qMap[optionId]) delete qMap[optionId]; else qMap[optionId] = true;
            return { ...(prev || {}), [questionId]: qMap };
        });
        // if disabled option selected -> clear
        cloneAndSet(list => {
            const idx = findIndexByQuestionId(questionId);
            if (idx < 0) return;
            const sa = list[idx];
            if (Array.isArray(sa?.selectedOptionIds) && sa.selectedOptionIds[0] === optionId) {
                list[idx] = { ...sa, selectedOptionIds: [], dirty: true };
            }
        });
    };

    const setQuestionHtml = (questionId, html) => setQuestionHtmlOverrides(p => ({ ...(p||{}), [questionId]: html }));
    const setOptionHtml = (questionId, optionId, html) => setOptionHtmlOverrides(p => ({ ...(p||{}), [questionId]: { ...(p?.[questionId]||{}), [optionId]: html } }));

    // Persist one open-ended answer immediately if needed (called on navigation)
    const persistOpenAnswer = async (questionId) => {
        if (!studentExamId || !questionId) return;
        const idx = findIndexByQuestionId(questionId);
        if (idx < 0) return;
        const sa = studentAnswers[idx];
        const qType = sa?.question?.questionType || sa?.question?.type;
        if (qType !== 'OpenEnded') return; // only open ended forced here
        if (!sa.dirty) return; // nothing changed
        await persistEntry(sa, idx);
    };

    const resetExam = () => {
        setCurrentQuestionIndex(0);
        setStudentAnswers([]);
        setReviewMarks([]);
        setDisabledOptions({});
        setQuestionHtmlOverrides({});
        setOptionHtmlOverrides({});
    };

    // Core persistence logic per entry
    const persistEntry = async (sa, idx) => {
        if (!studentExamId || !sa?.question?.id) return;
        const qid = sa.question.id;
        const qType = sa?.question?.questionType || sa?.question?.type;
        const hasOption = Array.isArray(sa.selectedOptionIds) && sa.selectedOptionIds.length > 0;
        const text = (sa.answerText || '').trim();
        const answered = qType === 'OpenEnded' ? (text.length > 0) : hasOption;

        try {
            if (sa.id && !answered) {
                // DELETE
                await studentAnswerService.delete(sa.id);
                cloneAndSet(list => { list[idx] = { ...sa, id: null, answerText: null, selectedOptionIds: [], dirty: false }; });
            } else if (!sa.id && answered) {
                // CREATE
                const payload = {
                    studentExam: studentExamId,
                    question: qid,
                    selectedOptionIds: hasOption ? sa.selectedOptionIds : null,
                    answerText: qType === 'OpenEnded' ? text : null,
                    objectIndex: Number(sa?.objectIndex) || 0,
                };
                const created = await studentAnswerService.create(payload);
                const newId = created?.id ?? null;
                cloneAndSet(list => { list[idx] = { ...sa, id: newId, answerText: qType === 'OpenEnded' ? text : null, dirty: false }; });
            } else if (sa.id && answered) {
                // UPDATE
                if (!sa.dirty) return; // nothing to update
                const payload = {
                    id: sa.id,
                    studentExam: studentExamId,
                    question: qid,
                    selectedOptionIds: hasOption ? sa.selectedOptionIds : null,
                    answerText: qType === 'OpenEnded' ? text : null,
                    objectIndex: Number(sa?.objectIndex) || 0,
                };
                await studentAnswerService.update(payload);
                cloneAndSet(list => { list[idx] = { ...sa, dirty: false, answerText: qType === 'OpenEnded' ? text : null }; });
            } else {
                // Unanswered & no id -> just clear dirty
                if (sa.dirty) cloneAndSet(list => { list[idx] = { ...sa, dirty: false }; });
            }
        } catch {
            // swallow; could add retry/notification
        }
    };

    // Effect: auto persist non-open-ended (option) answers & any dirty open-ended left
    useEffect(() => {
        if (!studentExamId) return;
        const dirtyIndices = [];
        studentAnswers.forEach((sa, i) => {
            if (sa?.dirty) {
                const qType = sa?.question?.questionType || sa?.question?.type;
                if (qType !== 'OpenEnded') dirtyIndices.push(i); // open ended handled on navigation / finish or here if left dirty
            }
        });
        if (dirtyIndices.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const idx of dirtyIndices) {
                if (cancelled) return;
                const sa = studentAnswers[idx];
                await persistEntry(sa, idx);
            }
        })();
        return () => { cancelled = true; };
    }, [studentAnswers, studentExamId]);

    // Initial fetch
    useEffect(() => {
        if (!studentExamId) {
            setStudentAnswers([]);
            return;
        }
        let mounted = true;
        setQuestionsLoading(true);
        studentAnswerService.findByStudentExamId(studentExamId)
            .then(res => {
                if (!mounted) return;
                const arr = Array.isArray(res) ? res : (res?.studentAnswerList || []);
                const ordered = [...arr].sort((a,b)=> (Number(a?.objectIndex)||0) - (Number(b?.objectIndex)||0))
                    .map(sa => ({
                        ...sa,
                        questionId: sa?.question?.id, // flat key for tables/lookup
                        answerText: sa?.answerText || null,
                        selectedOptionIds: Array.isArray(sa?.selectedOptionIds) ? sa.selectedOptionIds : [],
                        dirty: false,
                        objectIndex: Number(sa?.objectIndex) || 0,
                    }));
                setStudentAnswers(ordered);
            })
            .finally(() => mounted && setQuestionsLoading(false));
        return () => { mounted = false; };
    }, [studentExamId, studentAnswerService]);

    return (
        <ExamContext.Provider value={{
            exam,
            studentExamId,
            questions,
            studentAnswers,
            questionsLoading,
            currentQuestionIndex,
            setCurrentQuestionIndex,
            // compatibility
            answers,
            // setters
            setAnswer,
            setOpenAnswer,
            clearAnswer,
            // UI helpers
            reviewMarks,
            toggleReviewMark,
            disabledOptions,
            toggleOptionDisabled,
            questionHtmlOverrides,
            setQuestionHtml,
            optionHtmlOverrides,
            setOptionHtml,
            startTime,
            persistOpenAnswer,
            resetExam,
        }}>
            {children}
        </ExamContext.Provider>
    );
};
